import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CuoButton from "@/app/admin/_common/CuoButton";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import { Badge } from "@/app/admin/_common/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/app/admin/_common/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import type { PromoCode } from "@/app/_services/PromoService";
import { cn } from "@/app/_utils/cn";
import {
  ArrowLeft,
  Tag,
  Pencil,
  Power,
  PowerOff,
  Trash2,
  MoreVertical,
  MoreHorizontal,
} from "lucide-react";

export default function PromoDetailHeader({
  promo,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  promo: PromoCode | null;
  onEdit: () => void;
  onToggleActive: () => Promise<void> | void;
  onDelete: () => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const titleContent = (
    <h1 className="flex min-w-0 items-center gap-2 truncate text-[21px] font-bold leading-none tracking-tight text-foreground">
      <Tag className="size-5 flex-none text-foreground" />
      <span className="truncate font-mono">{promo?.code ?? "Promo Code"}</span>
    </h1>
  );

  const metaContent = (
    <div className="flex items-center gap-3 whitespace-nowrap text-[12.5px] text-muted-foreground">
      {promo && (
        <Badge
          variant="outline"
          className={cn(
            "gap-1.5 rounded-[7px] font-normal",
            promo.active
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-slate-200 bg-slate-50 text-slate-600"
          )}
        >
          <span
            className={cn(
              "size-1.5 flex-none rounded-full",
              promo.active ? "bg-emerald-500" : "bg-slate-400"
            )}
          />
          {promo.active ? "Active" : "Inactive"}
        </Badge>
      )}
      {promo && <span>{promo.discount_percent}% off</span>}
      {promo && (
        <span>
          {promo.shows.length === 0
            ? "All shows"
            : `${promo.shows.length} ${promo.shows.length === 1 ? "show" : "shows"}`}
        </span>
      )}
    </div>
  );

  const toggleLabel = promo?.active ? "Deactivate" : "Activate";
  const ToggleIcon = promo?.active ? PowerOff : Power;

  const mobileActionsSheet = (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Promo actions"
          className="inline-flex size-[30px] flex-none items-center justify-center rounded-lg border border-input bg-card text-foreground transition-colors hover:bg-accent hover:text-accent-foreground md:hidden"
        >
          <MoreVertical className="size-4 shrink-0" size={16} />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-xl bg-white p-0 md:hidden">
        <div className="flex flex-col pb-6 pt-6">
          <CuoButton
            variant="ghost"
            label="Edit"
            icon={<Pencil className="size-3.5" />}
            onClick={onEdit}
            disabled={!promo}
            className="h-11 w-full justify-start gap-2.5 rounded-none px-6 font-normal"
          />
          <CuoButton
            variant="ghost"
            label={toggleLabel}
            icon={<ToggleIcon className="size-3.5" />}
            onClick={onToggleActive}
            disabled={!promo}
            className="h-11 w-full justify-start gap-2.5 rounded-none px-6 font-normal"
          />
          <div className="mx-6 border-t border-border" />
          <CuoButton
            variant="ghost"
            label="Delete"
            icon={<Trash2 className="size-3.5" />}
            onClick={() => setConfirmDelete(true)}
            disabled={!promo}
            className="h-11 w-full justify-start gap-2.5 rounded-none px-6 font-normal text-red-600 hover:text-red-700"
          />
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="border-b border-border bg-card/60 px-5 pb-4 pt-5 backdrop-blur-[2px]">
      <button
        type="button"
        onClick={() => navigate("/admin/promos")}
        className="-mb-2 inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back to Promo Codes
      </button>

      {/* Mobile: title + actions trigger on one row, meta on its own row below */}
      <div className="flex flex-col gap-2 md:hidden">
        <div className="flex items-center justify-between gap-3">
          {titleContent}
          {mobileActionsSheet}
        </div>
        {metaContent}
      </div>

      {/* Desktop: title + meta grouped on the left, actions on the right */}
      {/* Fixed height so the row can't grow past its tallest control — without it the
          meta strip wraps to a second line at narrower widths. */}
      <div className="hidden h-[38px] items-center justify-between gap-3 md:flex">
        <div className="flex min-w-0 items-center gap-3">
          {titleContent}
          {metaContent}
        </div>
        <div className="flex flex-none items-center gap-2">
          <CuoButton
            variant="outline"
            label="Edit"
            icon={<Pencil className="size-3.5" />}
            onClick={onEdit}
            disabled={!promo}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              >
                <MoreHorizontal className="size-4 shrink-0" size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onToggleActive}>
                <ToggleIcon className="mr-2 size-4" />
                {toggleLabel}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="mr-2 size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CuoConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete promo code"
        message={`This action cannot be undone. Are you sure you want to delete ${
          promo?.code ?? "this promo code"
        }?`}
        onConfirm={onDelete}
        icon={<Trash2 />}
      />
    </div>
  );
}
